import dayjs from 'dayjs'

/**
 * Parses a Postgres interval string into a whole number of days.
 * Handles common forms: "7 days", "2 weeks", "1 mon", "1 month".
 * Returns 0 for unrecognised or empty values.
 * @param {string|null} durationStr
 * @returns {number}
 */
export function parseDurationDays(durationStr) {
  if (!durationStr || typeof durationStr !== 'string') return 0
  const s = durationStr.toLowerCase().trim()
  const dayMatch = s.match(/(\d+(?:\.\d+)?)\s*day/)
  if (dayMatch) return Math.round(parseFloat(dayMatch[1]))
  const weekMatch = s.match(/(\d+(?:\.\d+)?)\s*week/)
  if (weekMatch) return Math.round(parseFloat(weekMatch[1]) * 7)
  const monMatch = s.match(/(\d+(?:\.\d+)?)\s*mon/)
  if (monMatch) return Math.round(parseFloat(monMatch[1]) * 30)
  return 0
}

/**
 * Returns the maturing start date for a crop schedule entry.
 * @param {object} scheduleEntry
 * @returns {dayjs.Dayjs|null}
 */
export function getMaturingStart(scheduleEntry) {
  const phases = scheduleEntry?.phases || []
  const maturingPhase = phases.find(p => p.phase_type_code === 'maturing')
  if (!maturingPhase?.est_started_at) return null
  const d = dayjs(maturingPhase.est_started_at)
  return d.isValid() ? d : null
}

/**
 * Returns the harvest end date for a crop.
 * Harvest end = mature phase est_started_at + duration.
 * For successions: uses the last succession's mature start + duration.
 * @param {object} scheduleEntry
 * @param {Map} successionMap
 * @param {string} cropId
 * @returns {dayjs.Dayjs|null}
 */
export function getHarvestEnd(scheduleEntry, successionMap, cropId) {
  const phases = scheduleEntry?.phases || []
  const maturePhase = phases.find(p => p.phase_type_code === 'mature')
  if (!maturePhase?.est_started_at) return null

  const firstHarvest = dayjs(maturePhase.est_started_at)
  if (!firstHarvest.isValid()) return null

  const durationDays = parseDurationDays(maturePhase.duration)
  const succession = successionMap?.get(cropId)

  if (succession && succession.count > 0) {
    let offset = 0
    for (const child of succession.children) {
      offset += child.interval_days || 0
    }
    return firstHarvest.add(offset + durationDays, 'day')
  }

  return firstHarvest.add(durationDays, 'day')
}

/**
 * Calculates the overlap in days between two date windows.
 * @param {dayjs.Dayjs} aStart
 * @param {dayjs.Dayjs} aEnd
 * @param {dayjs.Dayjs} bStart
 * @param {dayjs.Dayjs} bEnd
 * @returns {{ days: number }|null}
 */
export function calculateOverlap(aStart, aEnd, bStart, bEnd) {
  if (!aStart || !aEnd || !bStart || !bEnd) return null
  const overlapStart = aStart.isAfter(bStart) ? aStart : bStart
  const overlapEnd = aEnd.isBefore(bEnd) ? aEnd : bEnd
  const days = overlapEnd.diff(overlapStart, 'day')
  if (days <= 0) return null
  return { days }
}

/**
 * Formats a day count into a human-readable duration string.
 * Scales: days → weeks → months.
 * @param {number} days
 * @returns {string}
 */
export function formatOverlapDuration(days) {
  if (days < 14) return `${days} day${days === 1 ? '' : 's'}`
  const weeks = Math.round(days / 7)
  if (weeks < 9) return `${weeks} week${weeks === 1 ? '' : 's'}`
  const months = Math.round(days / 30)
  return `${months} month${months === 1 ? '' : 's'}`
}
